import {Card, Grid, Title, Text} from '@mantine/core';
import {useEffect, useState} from 'react';

import {humanFileSize} from 'src/helpers/file.helper';
import {ILimitsResponse} from 'src/services/user';
import ProgressCard from 'src/components/core/ProgressCard';
import useTranslation from 'src/i18next.config';
import {useUserService} from 'src/services';

export function UserLimits() {
    const {t} = useTranslation();
    const {getLimits} = useUserService();
    const [limits, setLimits] = useState({} as ILimitsResponse);

    useEffect(function () {
        getLimits({
            error: error => {
                console.error(error);
            },
            success: res => {
                setLimits(res);
            },
        });
    }, []);

    function cleanDisplayNumber(value: number): number {
        if (value % 1) {
            return parseFloat(value.toFixed(2));
        } else {
            return value;
        }
    }

    function displayUsedTime() {
        if (!limits) return null;
        if (limits.enabledExecutionTime) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <ProgressCard
                        displayAsPercentage={false}
                        current={cleanDisplayNumber(limits.usedExecutionTime / 60)}
                        displayName={t('userLimits.usedExecutionTime')}
                        max={limits.maxExecutionTime/60}
                    ></ProgressCard>
                </Grid.Col>
            );
        }
        if (limits.usedExecutionTime != null) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <Card withBorder radius="md" p="lg">
                        <Text fz="lg">{t('userLimits.usedExecutionTime')}</Text>
                        <Text fz="lg" fw={500}>{cleanDisplayNumber(limits.usedExecutionTime / 60)} min</Text>
                    </Card>
                </Grid.Col>
            );
        }
        return null;
    }

    function displayUsedExecutions() {
        if (!limits) return null;
        if (limits.enabledMaxExecutions) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <ProgressCard
                        displayAsPercentage={false}
                        current={limits.usedExecutions}
                        displayName={t('userLimits.usedExecutions')}
                        max={limits.maxExecutions}
                    ></ProgressCard>
                </Grid.Col>
            );
        }
        if (limits.usedExecutions != null) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <Card withBorder radius="md" p="lg">
                        <Text fz="lg">{t('userLimits.usedExecutions')}</Text>
                        <Text fz="lg" fw={500}>{limits.usedExecutions}</Text>
                    </Card>
                </Grid.Col>
            );
        }
        return null;
    }

    function displayTimeout() {
        if (limits && limits.enabledMaxTimeout) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <Card withBorder radius="md" p="lg">
                        <Text fz="lg">{t('userLimits.maxTimeout')}</Text>
                        <Text fz="lg" fw={500}>
                            {limits.maxTimeout} {t('userLimits.minutes')}
                        </Text>
                    </Card>
                </Grid.Col>
            );
        }
    }

    function displayMaxFileSizePerExecution() {
        if (limits && limits.enabledMaxFilesSizePerExecution) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <Card withBorder radius="md" p="lg">
                        <Text fz="lg">{t('userLimits.maxFilesSizePerExecution')}</Text>
                        <Text fz="lg" fw={500}>
                            {humanFileSize(limits.maxFilesSizePerExecution)}
                        </Text>
                    </Card>
                </Grid.Col>
            );
        }
    }

    function displayTotalFilesSizeAllowedPerUser() {
        if (!limits) return null;
        if (limits.enabledTotalFilesSizeAllowedPerUser) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <ProgressCard
                        displayAsPercentage={false}
                        current={limits.usedStorage}
                        displayName={t('userLimits.totalFilesSize')}
                        displayFunction={humanFileSize}
                        max={limits.totalFilesSizeAllowedPerUser}
                    ></ProgressCard>
                </Grid.Col>
            );
        }
        if (limits.usedStorage != null) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <Card withBorder radius="md" p="lg">
                        <Text fz="lg">{t('userLimits.totalFilesSize')}</Text>
                        <Text fz="lg" fw={500}>{humanFileSize(limits.usedStorage)}</Text>
                    </Card>
                </Grid.Col>
            );
        }
        return null;
    }

    function displayMaxNumberOfUsers() {
        if (!limits) return null;
        if (limits.enabledMaxNumberOfUsers) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <ProgressCard
                        displayAsPercentage={false}
                        current={limits.numberOfUser}
                        displayName={t('userLimits.userNumber')}
                        max={limits.maxNumberOfUser}
                    ></ProgressCard>
                </Grid.Col>
            );
        }
        if (limits.numberOfUser != null) {
            return (
                <Grid.Col span={{base: 12, sm: 6}}>
                    <Card withBorder radius="md" p="lg">
                        <Text fz="lg">{t('userLimits.userNumber')}</Text>
                        <Text fz="lg" fw={500}>{limits.numberOfUser}</Text>
                    </Card>
                </Grid.Col>
            );
        }
        return null;
    }

    return (
        <>
            <Title order={2}>{t('limits')}</Title>

            <Grid style={{marginTop: 20}}>
                {displayUsedTime()}
                {displayUsedExecutions()}
                {displayTotalFilesSizeAllowedPerUser()}
                {displayMaxNumberOfUsers()}
                {displayMaxFileSizePerExecution()}
                {displayTimeout()}
            </Grid>
        </>
    );
}
